import { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useRouting } from '../../../utils/useRouting';
import { TextField, Box } from '@material-ui/core'; // Added Box and Button
import { startOfYear, endOfYear, differenceInDays, isSameYear } from 'date-fns';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import Chart from './Chart';
import API from '@aws-amplify/api';
import SetupHeader from '../SetupHeader';
import Grid from '@material-ui/core/Grid';
import LoaderButton from '../LoaderButton';
import Paper from '@material-ui/core/Paper';
import DateFnsUtils from '@date-io/date-fns';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';

const useStyles = makeStyles((theme) => ({
  container: {
    width: '95vw',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    [theme.breakpoints.down('md')]: {
      padding: 0,
      width: '90vw',
    },
  },
  content: {
    marginTop: theme.spacing(2),
  },
  toggle: {
    padding: '6px 16px',
  },
  toggleGroup: {
    marginTop: -2,
    marginLeft: theme.spacing(10),
    backGroundColor: 'white',
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
    },
    [theme.breakpoints.up('md')]: {
      height: '80%',
    },
  },
  paper: {
    padding: theme.spacing(2),
  },
  selectField: {
    background: 'rgba(0, 0, 0, 0.09)',
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    paddingLeft: 10,
    width: 120,
    marginLeft: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
    },
  },
}));

export default function Visualisierung({ isEmicon = false }: { isEmicon?: boolean }) {
  const classes = useStyles();

  const { currentDevice } = useRouting('Visualisierung', isEmicon);

  const [chartData, setChartData] = useState({
    coTMW: [],
    coHMW: [],
    noxTMW: [],
    noxHMW: [],
    tempTMW: [],
    tempHMW: [],
    noxAlarm: [],
    coAlarm: [],
    tempAlarm: [],
    tempMin: [],
    tempMax: [],
  });

  const [chartDataTermine, setChartDataTermine] = useState({
    allEvents: [],
  });

  const [chartMode, setChartMode] = useState('all');
  const [noData, setNoData] = useState(false);
  const [noDataTermine, setNoDataTermine] = useState(false);
  const [valueMode, setValueMode] = useState('hmw');
  const [isLoading, setIsLoading] = useState(false);

  // Input States
  const [searchMode, setSearchMode] = useState<'days' | 'year'>('days');
  const [days, setDays] = useState(30);
  const [finalDays, setFinalDays] = useState(30);
  const [selectedYear, setSelectedYear] = useState<Date | null>(new Date());
  const [chartMaxDate, setChartMaxDate] = useState<number>(Date.now());

  const buildParams = (customParams?: { amountOfDays?: number; start?: number; end?: number }) => {
    const params: any = { serialNumber: currentDevice.serialNumber, isEmicon };

    if (customParams?.start && customParams?.end) {
      params.start = customParams.start;
      params.end = customParams.end;
    } else {
      params.amountOfDays = customParams?.amountOfDays || days;
    }

    return params;
  };

  const fetchChartData = async (params: any) => {
    try {
      setNoData(false);
      const res = await API.get('emilog', 'chartData', { queryStringParameters: params });
      setChartData(res);
    } catch (err) {
      console.error('chartData error:', err);
      setNoData(true);
    }
  };

  const fetchChartDataTermine = async (params: any) => {
    try {
      setNoDataTermine(false);
      const res = await API.get('emilog', 'chartDataTermine', { queryStringParameters: params });
      setChartDataTermine(res);
    } catch (err) {
      console.error('chartDataTermine error:', err);
      setNoDataTermine(true);
    }
  };

  const fetchData = async (appliedDays: number, customParams?: { amountOfDays?: number; start?: number; end?: number }) => {
    if (!currentDevice?.serialNumber) return;

    setIsLoading(true);

    const params = buildParams(customParams);

    await Promise.all([
      fetchChartData(params),
      fetchChartDataTermine(params)
    ]);

    // ✅ use explicit value instead of stale state
    if (appliedDays !== undefined) {
      // setDays(appliedDays);
      setFinalDays(appliedDays);
    }

    setIsLoading(false);
  };

  const handleManualSearch = () => {
    setIsLoading(true);
    if (searchMode === 'year' && selectedYear) {
      // This handles the transition between CET and CEST (Summertime) automatically
      const start = startOfYear(selectedYear);
      let end = endOfYear(selectedYear);
      const today = new Date();

      // Check if the selected year is the current year
      if (isSameYear(selectedYear, today)) {
        // If it's this year, only go up to "now" to avoid empty space
        end = today;
      }
      // Calculate actual days in that specific year (365 or 366)
      const daysInYear = differenceInDays(end, start) + 1;
      setDays(daysInYear); // Update the state so the Chart knows the scale
      setChartMaxDate(end.getTime()); // Set chart boundary to Dec 31st of that year

      fetchData(daysInYear, {
        start: Math.floor(start.getTime() / 1000),
        end: Math.floor(end.getTime() / 1000)
      });
    } else {
      setChartMaxDate(Date.now()); // Set chart boundary to Dec 31st of that year
      fetchData(days, { amountOfDays: days });
    }
  };

  function handleChartToggle(event: any, newValue: string) {
    event.preventDefault();
    setChartMode(newValue);
  }

  function handleValueToggle(event: any, newValue: string) {
    event.preventDefault();
    setValueMode(newValue);
  }


  const renderInputs = () => (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Box display="flex" alignItems="center" gridGap={16} bgcolor="white" p={1} borderRadius={4}>
        {/* Mode Toggle */}
        <ToggleButtonGroup
          size="small"
          value={searchMode}
          exclusive
          onChange={(_, val) => val && setSearchMode(val)}
        >
          <ToggleButton value="days">Tage</ToggleButton>
          <ToggleButton value="year">Jahr</ToggleButton>
        </ToggleButtonGroup>

        {searchMode === 'days' ? (
          <TextField
            label="Anzahl Tage"
            type="number"
            value={days}
            variant="outlined"
            size="small"
            onChange={(e) => setDays(Number(e.target.value))}
            style={{ width: 140 }}
          />
        ) : (
          <KeyboardDatePicker
            views={["year"]}
            label="Jahr"
            inputVariant="outlined"
            size="small"
            value={selectedYear}
            onChange={(date) => setSelectedYear(date)}
            style={{ width: 140 }}
          />
        )}

        <LoaderButton
          isLoading={isLoading}            // Your new loading state
          text="Suchen"                  // The button label
          onClick={handleManualSearch}   // The search function
          variant="contained"            // Styling
          noMargin                       // To keep it aligned in your flex box
        />
      </Box>
    </MuiPickersUtilsProvider>
  );

  const renderToggle = () => (
    <ToggleButtonGroup
      exclusive
      value={chartMode}
      onChange={handleChartToggle}
      className={classes.toggleGroup}

    >
      <ToggleButton
        classes={{ root: classes.toggle }}
        value="all"
      >
        Übersicht TMW
      </ToggleButton>
      <ToggleButton
        classes={{ root: classes.toggle }}
        value="co"
      >
        CO
      </ToggleButton>
      <ToggleButton
        classes={{ root: classes.toggle }}
        value="nox"
      >
        NOx
      </ToggleButton>
      <ToggleButton
        classes={{ root: classes.toggle }}
        value="temp"
      >
        Temperatur
      </ToggleButton>
    </ToggleButtonGroup>
  );

  const renderValueToggle = () => (
    <ToggleButtonGroup
      exclusive
      value={valueMode}
      onChange={handleValueToggle}
      className={classes.toggleGroup}
    >
      <ToggleButton
        classes={{ root: classes.toggle }}
        value="hmw"
      >
        HMW
      </ToggleButton>
      <ToggleButton
        classes={{ root: classes.toggle }}
        value="tmw"
      >
        TMW
      </ToggleButton>

    </ToggleButtonGroup>
  );

  // Initial call - only on mount or device change
  useEffect(() => {
    fetchData(days, { amountOfDays: days });
  }, [currentDevice, isEmicon]);

  return (
    <Container className={classes.container} maxWidth="xl">
      <SetupHeader hideButtons isEmicon={isEmicon}>
        <Grid item xs={12} md="auto">
          {renderToggle()}
        </Grid>
        <Grid item md="auto">
          {renderInputs()}
        </Grid>

        {chartMode !== 'all' ? (
          <Grid item md="auto">
            {renderValueToggle()}
          </Grid>
        ) : (
          <>
          </>
        )}
      </SetupHeader>

      <Grid container spacing={2} className={classes.content} alignItems="center" justifyContent="center">
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            {noData ? (
              <Typography component="h6">Keine Messdaten im ausgewählten Zeitraum aufgezeichnet</Typography>
            ) : (
              chartMode === 'all' ? (
                <Grid container spacing={2}>
                  <Grid item sm={12} lg={4}>
                    <Chart type="co" chartData={chartData?.coTMW} days={finalDays} limits={chartData?.coAlarm} maxDate={chartMaxDate} />
                  </Grid>
                  <Grid item sm={12} lg={4}>
                    <Chart type="nox" chartData={chartData?.noxTMW} days={finalDays} limits={chartData?.noxAlarm} maxDate={chartMaxDate} />
                  </Grid>
                  <Grid item sm={12} lg={4}>
                    <Chart type="temp" chartData={chartData?.tempTMW} days={finalDays} limits={chartData?.tempAlarm} maxDate={chartMaxDate} />
                  </Grid>
                </Grid>
              ) : chartMode === 'co' ? (
                <Chart
                  type="co"
                  chartData={valueMode === 'hmw' ? chartData.coHMW : chartData.coTMW}
                  days={finalDays}
                  showHmw={valueMode === 'hmw'}
                  limits={chartData?.coAlarm}
                  fullSize
                  maxDate={chartMaxDate}
                />
              ) : chartMode === 'nox' ? (
                <Chart
                  type="nox"
                  chartData={valueMode === 'hmw' ? chartData.noxHMW : chartData.noxTMW}
                  days={finalDays} showHmw={valueMode === 'hmw'}
                  limits={chartData?.noxAlarm}
                  fullSize
                  maxDate={chartMaxDate}
                />
              ) : chartMode === 'temp' && (
                <Chart
                  type="temp"
                  chartData={valueMode === 'hmw' ? chartData.tempHMW : chartData.tempTMW}
                  days={finalDays} showHmw={valueMode === 'hmw'}
                  limits={chartData?.tempAlarm}
                  fullSize
                  maxDate={chartMaxDate}
                />
              )
            )}
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} className={classes.content} alignItems="center" justifyContent="center">
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            {noDataTermine ? (
              <Typography component="h6">Keine Termine im ausgewählten Zeitraum verfügbar</Typography>
            ) : (
              <Grid container spacing={2}>

                {/* The Encapsulated Event Timeline */}

                <Grid item xs={12}>
                  <Chart
                    isEventChart
                    type="events"
                    chartData={chartDataTermine?.allEvents || []}
                    days={finalDays}
                    maxDate={chartMaxDate}
                  />
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>

    </Container>
  );
}
